import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import {
  ChangeChannelRequest,
  IncomingQuotationDto,
  IncomingQuotationState,
  RejectIncomingQuotationRequest,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { InternalAPI } from 'services/APIs/InternalAPI/InternalAPI'
import { GetContext } from 'store/storeUtils'

export type IncomingQuotation = IncomingQuotationDto
export { IncomingQuotationState }

export const incomingQuotationRequestIds = {
  list: 'ListIncomingQuotations',
  detail: (quotationId: string) => `GetIncomingQuotation-${quotationId}`,
  accept: (quotationId: string) => `AcceptIncomingQuotation-${quotationId}`,
  reject: (quotationId: string) => `RejectIncomingQuotation-${quotationId}`,
  changeDecisionChannel: (quotationId: string) =>
    `ChangeIncomingQuotationDecisionChannel-${quotationId}`,
  upload: 'UploadIncomingQuotation',
  download: (quotationId: string) => `DownloadIncomingQuotation-${quotationId}`,
  downloadDecision: (quotationId: string) =>
    `DownloadIncomingQuotationDecision-${quotationId}`,
} as const

export class IncomingQuotationsClient extends InternalAPI {
  constructor(partyId: string, onRequestChange: OnRequestChangeCallback) {
    super(`/api/parties/${partyId}/quotations/incoming`, onRequestChange)
  }

  list(): Promise<IncomingQuotationDto[]> {
    return this.GetAsync({ relativePath: '', id: incomingQuotationRequestIds.list })
  }

  detail(quotationId: string): Promise<IncomingQuotationDto> {
    return this.GetAsync({
      relativePath: `/${quotationId}`,
      id: incomingQuotationRequestIds.detail(quotationId),
    })
  }

  accept(quotationId: string): Promise<void> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${quotationId}/accept`,
      id: incomingQuotationRequestIds.accept(quotationId),
    })
  }

  reject(
    quotationId: string,
    request: RejectIncomingQuotationRequest,
  ): Promise<void> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${quotationId}/reject`,
      data: request,
      id: incomingQuotationRequestIds.reject(quotationId),
    })
  }

  changeDecisionChannel(
    quotationId: string,
    request: ChangeChannelRequest,
  ): Promise<IncomingQuotationDto> {
    return this.PutAuthenticatedAsync({
      relativePath: `/${quotationId}/decision-channel`,
      data: request,
      id: incomingQuotationRequestIds.changeDecisionChannel(quotationId),
    })
  }

  upload(file: File, requestForQuotationId: string): Promise<unknown> {
    const data = new FormData()
    data.append('file', file)
    data.append('requestForQuotationId', requestForQuotationId)
    return this.PostAuthenticatedAsync({ relativePath: '/upload', data, id: incomingQuotationRequestIds.upload })
  }

  download(quotationId: string) {
    return this.DownloadAsync({ relativePath: `/${quotationId}/document`, id: incomingQuotationRequestIds.download(quotationId) })
  }

  downloadDecision(quotationId: string) {
    return this.DownloadAsync({ relativePath: `/${quotationId}/decision-document`, id: incomingQuotationRequestIds.downloadDecision(quotationId) })
  }
}

export class IncomingQuotationsController extends BaseController<IncomingQuotationsClient> {
  constructor() {
    super((onRequestChange) => {
      const { partyId } = GetContext()
      return new IncomingQuotationsClient(partyId, onRequestChange)
    })
  }

  private handle<T>(operation: () => Promise<T>): Promise<T> {
    return operation().catch((error) => Promise.reject(this.HandleError(error)))
  }

  list() {
    return this.handle(() => this.api.list())
  }

  detail(quotationId: string) {
    return this.handle(() => this.api.detail(quotationId))
  }

  accept(quotationId: string) {
    return this.handle(() => this.api.accept(quotationId))
  }

  reject(quotationId: string, reason?: string) {
    return this.handle(() => this.api.reject(quotationId, { reason }))
  }

  changeDecisionChannel(quotationId: string, request: ChangeChannelRequest) {
    return this.handle(() => this.api.changeDecisionChannel(quotationId, request))
  }

  upload(file: File, requestForQuotationId: string) {
    return this.handle(() => this.api.upload(file, requestForQuotationId))
  }

  download(quotationId: string) {
    return this.handle(() => this.api.download(quotationId))
  }

  downloadDecision(quotationId: string) {
    return this.handle(() => this.api.downloadDecision(quotationId))
  }
}
